import {
  InlineDatumReactComponentProps,
  useInlineDataEvents,
} from '@stripe-internal/inline-data';
import {view, Icon, css} from '@sail/ui/deprecated';
import {warning, info, chat, call, email} from '@sail/icons/react/Icon';
import {IconAsset} from '@sail/icons/types';
import {v4 as uuid} from 'uuid';

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ChatButtonOptions} from '@stripe-internal/conversations-ui';
import {defineMessages, useIntl} from 'react-intl';

import {RequestACallSection, RequestACallForm} from './RequestACallSection';
import {RACClientErrors} from './RequestACallForm';
import SendUsAnEmailSection, {
  EmailClientErrors,
  EmailForm,
} from '../helpers/SendUsAnEmailSection';

const messages = defineMessages({
  restartTheChat: {
    id: 'mse_inline_data.universal_help_channel_menu.restart_the_chat',
    description: 'Button used to restart the stripe support chat.',
    defaultMessage: 'Ask about something else',
  },
  sendError: {
    id: 'mse_inline_data.universal_help_channel_menu.send_error',
    description: 'Error when there is an issue attempting to a send a message',
    defaultMessage:
      'There was an issue selecting your option. Please check your internet connection and try again.',
  },
});

const POLL_MS = 5000;

type ChannelOption = {
  id: string;
  message: string;
  disabled?: boolean;
  icon?: string;
};

type ValueProps = {
  channel_options: Array<ChannelOption>;
  notice?: string;
  notice_type?: string;
  selected_option_id?: string;
  hide_restart_chat_option?: boolean;
  email_override?: string;
  pause_polling?: boolean;
  rac_form?: RequestACallForm;
  email_form?: EmailForm;
  is_rac_form_submitted?: boolean;
  is_email_form_submitted?: boolean;
  show_unauthenticated_email_field?: boolean;
  show_cc_emails_field?: boolean;
  initial_name?: string;
  initial_phone_number?: string;
  error_code?: RACClientErrors | EmailClientErrors;
};

const getIconFromName = (iconName: string): IconAsset | undefined => {
  switch (iconName) {
    case 'chat':
      return chat;
    case 'call':
      return call;
    case 'email':
      return email;
    default:
      return undefined;
  }
};

export function UniversalHelpChannelMenuNew(
  props: InlineDatumReactComponentProps,
) {
  const intl = useIntl();

  const {datum, onDone, isDone, fetch, action, actionError} = props;
  const value = datum.value as ValueProps;
  const {
    channel_options: channelOptions,
    notice,
    notice_type: noticeType,
    selected_option_id: selectedOptionId,
    hide_restart_chat_option: hideRestartChatOption,
    email_override: emailOverride,
    pause_polling: pausePolling,
    rac_form: requestACallForm,
    is_rac_form_submitted: isRacFormSubmitted,
    email_form: emailForm,
    is_email_form_submitted: isEmailFormSubmitted,
    show_unauthenticated_email_field: showUnauthenticatedEmailField,
    show_cc_emails_field: showCcEmailsField,
    initial_name: initialName,
    initial_phone_number: initialPhoneNumber,
    error_code: errorCode,
  } = value;

  const [restartChatOptionDisabled] = useState<boolean>(
    !!hideRestartChatOption,
  );

  const events = useInlineDataEvents();

  const nonce = useRef<string>(uuid()).current;

  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    selectedOptionId,
  );

  const [err, setErr] = useState<string | undefined>();

  // Update the selected option if we are provided one from the props
  useEffect(() => {
    // If an option was previously selected, don't unset it. If we're intentionally
    // unsetting the value, we'll call the setter to do that
    setSelectedOption((prev) => prev || selectedOptionId);
  }, [selectedOptionId]);

  useEffect(() => {
    if (!selectedOptionId || isDone) {
      return;
    }

    const isRacFormInProgress =
      selectedOptionId === 'channel_rac' && !isRacFormSubmitted;
    const isEmailFormInProgress =
      selectedOptionId === 'channel_email' && !isEmailFormSubmitted;

    if (isRacFormInProgress || isEmailFormInProgress) {
      return;
    }

    onDone({
      type: 'success',
      extra: {
        outcome: selectedOption,
        value: {
          ...value,
          selected_option_id: selectedOption,
        },
      },
    });
  }, [
    isDone,
    isEmailFormSubmitted,
    isRacFormSubmitted,
    onDone,
    selectedOption,
    selectedOptionId,
    value,
  ]);

  useEffect(() => {
    // don't poll while on the forms
    // we don't want to add complexity with potential action responses
    const noFetch =
      isDone ||
      pausePolling ||
      isRacFormSubmitted ||
      isEmailFormSubmitted ||
      (selectedOption &&
        ['channel_email', 'channel_rac'].includes(selectedOption));

    if (noFetch) {
      return;
    }

    const timeout = setTimeout(() => {
      if (!noFetch) {
        fetch({
          poll: true,
        });
      }
    }, POLL_MS);

    return () => clearTimeout(timeout);
  }, [
    isDone,
    fetch,
    pausePolling,
    selectedOption,
    isRacFormSubmitted,
    isEmailFormSubmitted,
  ]);

  const selectOption = useCallback(
    async (option: ChannelOption) => {
      events.emit({name: 'inline-data-trigger-scroll-to-top'});

      setSelectedOption(option.id);

      const result = await events.emitWithResult({
        name: 'inline-data-action',
        data: {
          purpose: 'channel_selection',
          channelID: option.id,
        },
        nonce,
      });

      if (result.error) {
        setErr(intl.formatMessage(messages.sendError));
        setSelectedOption(undefined);
      } else {
        action({selected_option_id: option.id});
      }
    },
    [action, events, intl, nonce],
  );

  const handleCancelForm = useCallback(() => {
    setSelectedOption(undefined);
    action({selected_option_id: undefined});
  }, [action]);

  const initialConvOptions = useMemo(
    () =>
      channelOptions
        .concat({
          id: 'channel_restart',
          message: intl.formatMessage(messages.restartTheChat),
          disabled: !!restartChatOptionDisabled,
        })
        .filter((option) => !option.disabled)
        .map((option) => {
          return {
            value: option.id,
            label: option.message,
            onPress: () => selectOption(option),
            icon: option.icon ? getIconFromName(option.icon) : undefined,
          };
        }),
    [channelOptions, intl, restartChatOptionDisabled, selectOption],
  );

  const convOptions = useMemo(
    () =>
      initialConvOptions.map((option) => {
        if (emailOverride) {
          if (option.value === 'channel_email' && emailOverride) {
            const originalLabel = option.label;

            let timeSuffix = '';
            if (originalLabel.includes('·')) {
              timeSuffix = originalLabel.split('·')[1].trim();
            } else if (
              originalLabel.includes('(') &&
              originalLabel.includes(')')
            ) {
              const match = originalLabel.match(/\((.*?)\)/);
              if (match) {
                timeSuffix = match[1].trim();
              }
            }

            return {
              ...option,
              label: `${emailOverride} · ${timeSuffix}`,
            };
          }
        }
        return option;
      }),
    [emailOverride, initialConvOptions],
  );

  return (
    <view.div>
      <view.div
        css={{
          margin: 'small',
          stack: 'y',
          gap: 'medium',
        }}
      >
        {notice && noticeType ? (
          <view.div
            role="alert"
            css={{
              stack: 'x',
              alignY: 'center',
              gap: 'small',
              color: noticeType === 'warning' ? 'attention' : 'info',
            }}
          >
            <Icon
              icon={noticeType === 'warning' ? warning : info}
              size="xsmall"
              aria-hidden
              css={{
                alignSelf: css.value('baseline'),
                flexShrink: css.value('0'),
                paddingTop: 'xxsmall',
                marginTop: 'xxsmall',
              }}
            />
            <view.div>{notice}</view.div>
          </view.div>
        ) : null}

        <view.div
          className="uh-conversations-button-options-message-row"
          data-message-type="user"
          css={{alignX: 'end', stack: 'y', gap: 'small'}}
        >
          <ChatButtonOptions
            options={convOptions}
            selectedOptionId={selectedOption}
            error={err}
            animated
          />
        </view.div>
      </view.div>

      <RequestACallSection
        action={action}
        nonce={nonce}
        initialName={initialName || ''}
        initialPhoneNumber={initialPhoneNumber || ''}
        requestACallForm={requestACallForm}
        isSectionVisible={
          selectedOptionId === 'channel_rac' ||
          selectedOption === 'channel_rac' ||
          !!isRacFormSubmitted
        }
        isRacFormSubmitted={isDone || !!isRacFormSubmitted}
        handleCancel={handleCancelForm}
        clientError={
          errorCode?.startsWith('rac-')
            ? (errorCode as RACClientErrors)
            : undefined
        }
        serverError={actionError}
      />

      <SendUsAnEmailSection
        action={action}
        nonce={nonce}
        handleCancelForm={handleCancelForm}
        emailForm={emailForm}
        isSectionVisible={
          selectedOptionId === 'channel_email' ||
          selectedOption === 'channel_email' ||
          !!isEmailFormSubmitted
        }
        isEmailFormSubmitted={isDone || !!isEmailFormSubmitted}
        showUnauthenticatedEmailField={showUnauthenticatedEmailField}
        showCcEmailsField={showCcEmailsField}
        clientError={
          errorCode?.startsWith('email-')
            ? (errorCode as EmailClientErrors)
            : undefined
        }
        serverError={actionError}
      />
    </view.div>
  );
}
